"use client"

import type { Tower } from "@/types/tower.types"
import { towerTypes, getTowerClassName } from "@/data/tower-types"
import type { UpgradeModifiers } from "@/types/upgrade.types"
import { ArrowUpCircle, HandCoins } from "lucide-react"
import { getFootmanConfig, ATTACK_SPEED } from "@/data/barracks-config"

// Check if tower type uses unit portraits (archer, mage, siege)
const hasUnitPortrait = (type: string): boolean => {
  return type === "archer" || type === "mage" || type === "siege"
}

interface TowerInfoPanelProps {
  tower: Tower
  towerScreenPosition?: { x: number; y: number }  // Screen coordinates for positioning above tower
  money: number
  upgradeModifiers?: UpgradeModifiers
  isLastWaveStarted?: boolean  // True when last wave has been called - selling disabled
  onUpgrade: () => void
  onSell: () => void
  onClose: () => void
}

export const TowerInfoPanel = ({ tower, towerScreenPosition, money, upgradeModifiers, isLastWaveStarted = false, onUpgrade, onSell, onClose }: TowerInfoPanelProps) => {
  const config = towerTypes[tower.type]
  const maxLevel = config.upgrades.length
  const canUpgrade = tower.level < maxLevel
  const upgradeCost = canUpgrade ? config.upgrades[tower.level].cost : 0
  const canAffordUpgrade = money >= upgradeCost

  const totalInvested = config.upgrades.slice(0, tower.level).reduce((sum, upgrade) => sum + upgrade.cost, 0)
  const sellValue = Math.floor(totalInvested * 0.7)

  // Calculate modified stats based on upgrade tree
  const getDamageModifier = () => {
    if (!upgradeModifiers) return 1
    if (tower.type === "archer") return upgradeModifiers.archerDamage
    if (tower.type === "mage") return upgradeModifiers.mageDamage
    if (tower.type === "siege") return upgradeModifiers.siegeDamage
    return 1
  }
  const getRangeModifier = () => {
    if (!upgradeModifiers) return 1
    if (tower.type === "archer") return upgradeModifiers.archerRange
    return 1
  }
  const getSpeedModifier = () => {
    if (!upgradeModifiers) return 1
    if (tower.type === "archer") return upgradeModifiers.archerSpeed
    return 1
  }

  const damageModifier = getDamageModifier()
  const rangeModifier = getRangeModifier()
  const speedModifier = getSpeedModifier()

  // Calculate base and bonus values
  const baseDamage = tower.damage
  const bonusDamage = Math.floor(tower.damage * damageModifier) - baseDamage
  const baseRange = tower.range
  const bonusRange = Math.floor(tower.range * rangeModifier) - baseRange
  const baseFireRate = Math.round((60 / tower.attackSpeed) * 10) / 10
  const modifiedFireRate = Math.round(((60 / tower.attackSpeed) * speedModifier * 10) / 10)
  const bonusFireRate = Math.round((modifiedFireRate - baseFireRate) * 10) / 10

  const handleUpgrade = () => {
    console.log("[v0] TowerInfoPanel: Upgrade clicked for tower", tower.id, "level", tower.level)
    onUpgrade()
  }

  const handleSell = () => {
    console.log("[v0] TowerInfoPanel: Sell clicked for tower", tower.id, "sellValue", sellValue)
    onSell()
  }

  // Position above tower if screen position provided, otherwise top-right corner
  const positionStyle = towerScreenPosition
    ? {
        left: towerScreenPosition.x,
        top: Math.max(10, towerScreenPosition.y - 220),  // Above tower, with minimum top margin
        transform: "translateX(-50%)",
      }
    : {
        top: 16,
        right: 16,
      }

  return (
    <div
      id="tower-info-panel"
      className="absolute bg-game-surface-overlay text-foreground p-3 rounded-lg shadow-lg border-2 border-game-border min-w-[180px] z-50 pointer-events-auto"
      style={positionStyle}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold text-base">{config.name}</h3>
        <button
          id="close-tower-info"
          onClick={onClose}
          onTouchEnd={(e) => {
            e.stopPropagation()
            onClose()
          }}
          className="text-muted-foreground hover:text-foreground transition"
          style={{ touchAction: "manipulation" }}
        >
          ✕
        </button>
      </div>

      <div className="space-y-1.5 mb-3 text-xs">
        {tower.type === "barracks" ? (
          // Barracks-specific stats: show soldier name above level, then soldier stats
          (() => {
            const soldierConfig = getFootmanConfig(tower.level)
            const soldierHpModifier = upgradeModifiers?.soldierHp ?? 1
            const baseHealth = soldierConfig.health
            const bonusHealth = Math.floor(baseHealth * soldierHpModifier) - baseHealth
            return (
              <>
                <div className="font-semibold text-amber-400 text-left">{soldierConfig.name}</div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Level:</span>
                  <span className="font-semibold">{tower.level} / {maxLevel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Health:</span>
                  <span className="font-semibold">
                    {baseHealth}
                    {bonusHealth > 0 && <span className="text-green-400 ml-1">+{bonusHealth}</span>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Damage:</span>
                  <span className="font-semibold">{soldierConfig.damage.min}-{soldierConfig.damage.max}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Armor:</span>
                  <span className="font-semibold">{soldierConfig.armor}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Atk Speed:</span>
                  <span className="font-semibold">{Math.round((60 / ATTACK_SPEED) * 10) / 10}/s</span>
                </div>
              </>
            )
          })()
        ) : (
          // Standard tower stats (archer, mage, siege, etc.)
          <>
            {/* For archer/mage/siege: show unit name */}
            {hasUnitPortrait(tower.type) && tower.name && (
              <div className="font-semibold text-amber-400 text-left">{tower.name}</div>
            )}
            {/* Level with class name for archer/mage/siege */}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Level:</span>
              <span className="font-semibold">
                {tower.level} {hasUnitPortrait(tower.type) && getTowerClassName(tower.type, tower.level)}
              </span>
            </div>
            {/* Hide damage for shrines */}
            {tower.type !== "shrine" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Damage:</span>
                <span className="font-semibold">
                  {baseDamage}
                  {bonusDamage > 0 && <span className="text-green-400 ml-1">+{bonusDamage}</span>}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{tower.type === "shrine" ? "Aura Radius:" : "Range:"}</span>
              <span className="font-semibold">
                {baseRange}
                {bonusRange > 0 && <span className="text-green-400 ml-1">+{bonusRange}</span>}
              </span>
            </div>
            {/* Hide fire rate for shrines */}
            {tower.type !== "shrine" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fire Rate:</span>
                <span className="font-semibold">
                  {baseFireRate}/s
                  {bonusFireRate > 0 && <span className="text-green-400 ml-1">+{bonusFireRate}</span>}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex gap-2">
        {canUpgrade && (
          <button
            id="upgrade-tower-button"
            onClick={handleUpgrade}
            onTouchEnd={(e) => {
              e.stopPropagation()
              if (canAffordUpgrade) handleUpgrade()
            }}
            disabled={!canAffordUpgrade}
            className={`
              flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-semibold text-xs transition
              ${canAffordUpgrade ? "bg-emerald-600 hover:bg-emerald-500" : "bg-muted opacity-50 cursor-not-allowed"}
            `}
            style={{ touchAction: "manipulation" }}
          >
            <ArrowUpCircle size={14} />
            <img src="/images/icon-coins.png" alt="coins" width={12} height={12} className="object-contain" />
            <span className="text-white">{upgradeCost}</span>
          </button>
        )}
        {!canUpgrade && <div className="flex-1 text-center text-game-success text-xs py-1.5">Max Level!</div>}
        <div className="relative flex-1 group">
          <button
            id="sell-tower-button"
            onClick={isLastWaveStarted ? undefined : handleSell}
            onTouchEnd={(e) => {
              e.stopPropagation()
              if (!isLastWaveStarted) handleSell()
            }}
            disabled={isLastWaveStarted}
            className={`w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-semibold text-xs transition ${
              isLastWaveStarted
                ? "bg-muted opacity-50 cursor-not-allowed"
                : "bg-game-danger hover:bg-game-danger-hover"
            }`}
            style={{ touchAction: "manipulation" }}
          >
            <HandCoins size={14} />
            <img src="/images/icon-coins.png" alt="coins" width={12} height={12} className="object-contain" />
            <span className="text-white">{sellValue}</span>
          </button>
          {/* Tooltip for disabled sell button */}
          {isLastWaveStarted && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              <div className="bg-slate-900 border border-slate-600 rounded px-2 py-1 text-[10px] text-slate-300 whitespace-nowrap">
                Final wave started
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-900 border-r border-b border-slate-600 rotate-45" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
